import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { Router } from '@angular/router';
import { AuthenticationServiceImpl } from '../../../core/authentication/authentication-service-impl.service';
import { getCookieValue } from '../../../common/string-util';
import { Subscription } from 'rxjs';
import { FeedbackDialogComponent } from './feedback-dialog/feedback-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  protected readonly environment = environment;
  public userName = ''; // Class variable userName
  public greeting = '';
  private observableSubscriptions: Subscription[] = [];
  private changeDetector: ChangeDetectorRef | undefined;

  constructor(private readonly router: Router, private readonly authenticationService: AuthenticationServiceImpl, changeDetector: ChangeDetectorRef, private dialog: MatDialog) {
    this.changeDetector = changeDetector;
  }

  /**
   * Lifecycle hook that is called after the component is initialized.
   *
   * - Subscribes to the `getPrincipal` observable from the `AuthenticationServiceImpl` to retrieve the user's principal.
   * - If a principal is available, it sets the `userName` property to the principal value.
   * - Retrieves the value of the `VSPIDC_GREETING` cookie using the `getCookieValue` utility function.
   *   If the cookie is not found, defaults the `greeting` property to 'Greeting...'.
   */
  ngOnInit(): void {
    // Subscribe to the authentication service's principal observable
    this.observableSubscriptions.push(
      this.authenticationService.getPrincipal.subscribe(principal => {
        if (principal) {
          // Set the userName property to the principal value if available
          this.userName = this.authenticationService.principal;
        }
        this.changeDetector?.detectChanges();
      })
    );

    // Retrieve the greeting from the cookie or use a default value
    const greetingCookie = getCookieValue('VSPIDC_GREETING');
    if (greetingCookie) {
      this.greeting = 'Welcome ' + greetingCookie.split('|')[0];
    } else {
      this.greeting = 'Welcome to Eyefinity';
    }
  }

  /**
   * Overrides the `ngOnDestroy` lifecycle hook to clean up subscriptions.
   *
   * This method ensures that all subscriptions in the `observableSubscriptions` array
   * are unsubscribed to prevent memory leaks when the component is destroyed.
   * Additionally, logs the cleanup process for debugging purposes.
   */

  ngOnDestroy(): void {
    console.log('Cleaning up subscriptions in HeaderComponent...');
    this.observableSubscriptions.forEach(subscription => {
      subscription.unsubscribe();
      console.log('Unsubscribed from:', subscription);
    });
    // Add any cleanup logic here, such as unsubscribing from observables
  }

  navigate(url: string) {
    this.router.navigate(['/redirect', url]);
  }

  getChangePasswordUrl(): string {
    return `${environment.changePassword}${encodeURIComponent(this.userName || '')}`;
  }

  logout() {
    window.location.href = environment.logoutRedirectUrl;
  }

  isVspProvider() {
    const officeid = getCookieValue('VSPIDC_OFFICEID') || '';
    return !officeid.startsWith('EFAN');
  }

  openFeedbackDialog(): void {
    this.dialog.open(FeedbackDialogComponent, {
      width: '720px',
      maxWidth: '95vw',
      disableClose: false,
    });
  }

  redirectToEBuy(): void {
    const host = window.location.host;
    let classicEyefinityHost = '';
    const hostname = host.split(":")[0];

    if(/^localhost/.test(hostname)) {
      classicEyefinityHost = 'https://secureb-stc.eyefinity.com';
    }
    else if(host == 'portal-test.eyefinity.com' || host == 'elab-test.eyefinity.com') {
      classicEyefinityHost = 'https://secureb-stc.eyefinity.com';
    }
    else if(host == 'portal-staging.eyefinity.com' || host == 'elab-staging.eyefinity.com') {
      classicEyefinityHost = 'https://secureb-st.eyefinity.com';
    }
    else if(host == 'portal.eyefinity.com' || host == 'elab.eyefinity.com') {
      classicEyefinityHost = 'https://secureb.eyefinity.com';
    }

    const resource = "/darwin/eBuyHome.do?method=eBuyHome";
    const url = `${classicEyefinityHost}${resource}`;
    window.location.href = url;
  }
}
